import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { ChordsRow } from "../../../Entities/SongModel";
import ChordViewer, { normalizeChord } from "../../../Components/ChordViewer";
import { isChordToken } from "../../../utils/songEditorUtils";
import { SongCapoKeyBar } from "./SongCapoKeyBar";

interface SongBodyProps {
  rows: ChordsRow[];
  fontChange: number;
  isHebrew: boolean;
  customChords?: Record<string, any>;
  capo?: string;
  songKey?: string;
}

export const SongBody: React.FC<SongBodyProps> = ({
  rows,
  fontChange,
  isHebrew,
  customChords,
  capo,
  songKey,
}) => {
  const [selectedChord, setSelectedChord] = useState<string | null>(null);

  return (
    <Box
      sx={{
        fontFamily: "'Courier New', Courier, monospace",
        whiteSpace: "pre-wrap",
        lineHeight: 1.35,
        fontSize: {
          xs: `calc(0.85rem + ${fontChange}px)`,
          sm: `calc(0.95rem + ${fontChange}px)`,
          md: `calc(1.05rem + ${fontChange}px)`,
        },
        direction: isHebrew ? "rtl" : "ltr",
        textAlign: isHebrew ? "right" : "left",
        pb: 0,
        pt: 0,
        mb: 0,
      }}
    >
      <ChordViewer
        name={selectedChord}
        customChords={customChords}
        onClose={() => setSelectedChord(null)}
      />
      <SongCapoKeyBar capo={capo} songKey={songKey} isHebrew={isHebrew} />
      {rows.map((row, i) => {
        const chords = isHebrew ? row.chords?.trimStart() : row.chords;

        return (
          <Box
            key={i}
            data-testid="song-row"
            className="song-row"
            sx={{ mb: { xs: 0.5, sm: 0.75 }, "&:last-child": { mb: 0 } }}
          >
            {row.header && (
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="text.primary"
                mb={0.25}
                mt={1}
                sx={{
                  textAlign: isHebrew ? "right" : "left",
                  fontSize: { xs: "0.9rem", sm: "1.05rem" },
                }}
              >
                {row.header}
              </Typography>
            )}
            {chords && (
              <Box
                data-testid="song-chords"
                dir="ltr"
                sx={{
                  color: "#2563eb",
                  fontWeight: "bold",
                  textDecoration: "none",
                  borderBottom: "none",
                  direction: "ltr",
                  whiteSpace: isHebrew ? "pre" : "pre-wrap",
                  textAlign: isHebrew ? "right" : "left",
                  unicodeBidi: "isolate",
                }}
              >
                {chords.split(/(\s+)/).map((token, tokenIndex) => {
                  const isCustom = Boolean(
                    customChords && (customChords[token] || customChords[normalizeChord(token)])
                  );
                  const isChord = isCustom || isChordToken(token, customChords);

                  if (isChord) {
                    return (
                      <button
                        key={tokenIndex}
                        type="button"
                        onClick={() => setSelectedChord(token)}
                        aria-label={`Show ${token} chord`}
                        style={{
                          font: "inherit",
                          color: isCustom ? "#7c3aed" : "inherit",
                          fontWeight: isCustom ? 800 : "bold",
                          letterSpacing: "inherit",
                          border: 0,
                          borderBottom: isCustom ? "2px solid #7c3aed" : "1px dotted currentColor",
                          padding: isCustom ? "1px 3px" : 0,
                          borderRadius: isCustom ? 3 : 0,
                          backgroundColor: isCustom ? "#f5f3ff" : "transparent",
                          cursor: "pointer",
                          direction: "ltr",
                          unicodeBidi: "isolate",
                        } as React.CSSProperties}
                      >
                        {token}
                      </button>
                    );
                  }

                  return (
                    <span
                      key={tokenIndex}
                      style={{ direction: "ltr", unicodeBidi: "isolate" } as React.CSSProperties}
                    >
                      {token}
                    </span>
                  );
                })}
              </Box>
            )}
            {row.text && (
              <Box sx={{ color: "#1e293b", textAlign: isHebrew ? "right" : "left" }}>
                {row.text}
              </Box>
            )}
            {row.tab && row.tab.length > 0 && (
              <Box
                sx={{
                  direction: "ltr",
                  textAlign: "left",
                  whiteSpace: "pre",
                  overflowX: "auto",
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  p: { xs: 0.5, sm: 1 },
                  borderRadius: 1,
                  my: 0.5,
                  fontSize: "0.85em",
                }}
              >
                {row.tab.map((line, j) => (
                  <Box key={j}>{line}</Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
